import type { Measurement } from '@cognite/apm-client';
import type { DirectRelationReference, Timeseries } from '@cognite/sdk';

import { extractSourceableFields } from '../shared-utils/sourceable-utils';

import type { MeasurementUpsertType } from './cdm-types';

const toDirectRelationshipFormat = (
  timeseries: Timeseries | null | undefined,
  appDataInstanceSpace: string
): DirectRelationReference | undefined | null => {
  if (timeseries === null) return null;
  return timeseries?.externalId
    ? {
        space: appDataInstanceSpace,
        externalId: timeseries.externalId,
      }
    : undefined;
};

/**
 * Maps a Measurement to a MeasurementUpsertType for upsert, stripping
 * fields that are not part of the instance write:
 * - `createdBy`, `updatedBy` — deprecated InFieldUser fields excluded to prevent
 *   PII from being written to data model storage (see extractSourceableFields).
 * - `createdTime`, `lastUpdatedTime`, `space` — server-managed; excluded from writes.
 * - `timeseries` — converted from Timeseries object to DirectRelationship.
 * @param measurement - The measurement to map.
 * @param appDataInstanceSpace - The space used to resolve the timeseries direct relation.
 * @returns A payload ready for writing to the Cognite data model.
 */
export const mapMeasurementToUpsertType = (
  measurement: Measurement,
  appDataInstanceSpace: string
): MeasurementUpsertType => ({
  ...extractSourceableFields(measurement),
  externalId: measurement.externalId,
  type: measurement.type,
  title: measurement.title,
  description: measurement.description,
  timeseries: toDirectRelationshipFormat(
    measurement.timeseries,
    appDataInstanceSpace
  ),
  measuredAt: measurement.measuredAt,
  min: measurement.min,
  max: measurement.max,
  numericReading: measurement.numericReading,
  stringReading: measurement.stringReading,
  order: measurement.order,
  options: measurement.options,
});
